/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {DOCUMENT, Inject, Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, Subject} from "rxjs";
import {HttpClient} from '@angular/common/http';
// CHÚ Ý: Luôn import trực tiếp từ file nguồn (ví dụ: ./app.service),
// KHÔNG import từ barrel "@core/services" để tránh lỗi Circular Dependency.
import {AppService} from "@core/services/app.service";
import {SocketIoService} from "@core/services/socket-io.service";
import {DurableObjectService} from "@core/realtime/durable-object.service";

import {Router} from "@angular/router";
import {isPlatformBrowser} from "@angular/common";
import {PermissionService} from "./permission.service";

import {STORAGE_KEY} from '@core/constants';
import {IUserHeader, ROLES} from '@shared/models/user';
import {environment} from '@environments/environment';
import {isArray} from '@shared/utils';
import {IBusinessOption} from '@app/shared/models';
import {ROLES_NEW} from './auth.constants';


export interface IPage {
  id: any;
  businessId?: any;
  channel?: any;
  name?: string;
  avatar?: string;
  token?: string;
  isSyncedData?: any;
  settings?: any;
  isManagePage?: boolean;
  /** Facebook Conversions API dataset ID — dùng để kiểm tra kết nối Meta CAPI */
  fbDatasetId?: string;
  zaloMobile?: string;
  aliasName?: string;
}

export interface Auth {
  trackingInsta?: any;
  uid?: string;
  posUid?: string;
  displayName?: string;
  fbUid?: string;
  fbName?: string;
  createdTime?: string;
  announcements?: string;
  role?: number;
  userType?: number;
  jwt?: string;
  settings?: any;
  business?: any;
  businesses?: any;
  // Các doanh nghiệp có quyền sử dụng
  mBids?: Array<any>;
  // Tất cả các doanh nghiệp liên quan
  aBids?: string;
  bids?: string;
  // Tất cả các pages
  pageIds?: Array<any>;
  pages?: {
    [key: string]: IPage
  };
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  protected auth: Auth = {};

  // Chứa Page mà hội thoại đang xem => để biết hội thoại thuộc page nào.
  private _pageDisplay: any = new BehaviorSubject('');
  private _isSelectPage = new BehaviorSubject(false);
  private _activeBusiness = new BehaviorSubject<IBusinessOption | null>(null);
  // Danh sách các doanh nghiệp mà người dùng có quyền quản lý
  private _businesses = new BehaviorSubject<IBusinessOption[]>([]);
  /** Tín hiệu phát khi auth thay đổi (chọn page, đổi business, ...) — services subscribe để reset + reload */
  private _onAuthChanged$ = new Subject<void>();

  /**
   * Doanh nghiệp hiện tại đang hoạt động (Sử dụng interface IBusinessOption)
   */
  public get businessCurrent(): IBusinessOption | null {
    return this._activeBusiness.value;
  }

  public set businessCurrent(value: IBusinessOption | null | any) {
    this.activeBusiness = value;
  }

  constructor(
    private _app: AppService,
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: object,
    private io: SocketIoService,
    private http: HttpClient,
    private injector: Injector,
    private doService: DurableObjectService,
  ) {
    // Tự động khôi phục phiên làm việc từ localStorage khi ứng dụng khởi chạy
    if (isPlatformBrowser(this.platformId)) {
      this.restoreSession();
    }
  }

  /**
   * Khôi phục session từ localStorage
   */
  private restoreSession() {
    try {
      const usr = localStorage.getItem(STORAGE_KEY.usr);
      if (usr) {
        const authData = JSON.parse(usr);
        this.setAuthDataService(authData);
      }
    } catch (e) {
      console.error('AuthService: Không thể khôi phục session', e);
    }
  }

  /**
   * Lấy doanh nghiệp đang hoạt động (giá trị hiện tại)
   */
  get activeBusiness(): IBusinessOption | null {
    return this._activeBusiness.value;
  }

  /**
   * Stream dữ liệu doanh nghiệp đang hoạt động để các component có thể subscribe
   */
  get activeBusiness$() {
    return this._activeBusiness.asObservable();
  }

  /**
   * Stream danh sách doanh nghiệp có quyền quản lý
   */
  get businesses$() {
    return this._businesses.asObservable();
  }

  /**
   * Tín hiệu phát khi dữ liệu auth thay đổi (chọn page, đổi business).
   * Services (tags, quick message, settings, ...) subscribe để reset + reload dữ liệu.
   */
  get onAuthChanged$() {
    return this._onAuthChanged$.asObservable();
  }


  /**
   * Thiết lập doanh nghiệp đang hoạt động
   * @param business Thông tin doanh nghiệp hoặc null
   */
  set activeBusiness(business: IBusinessOption | null | any) {
    // Cập nhật giá trị mới vào Subject
    this._activeBusiness.next(business);

    // Lưu vào localStorage để duy trì trạng thái khi reload trang
    if (isPlatformBrowser(this.platformId) && business) {
      localStorage.setItem('activeBusiness', JSON.stringify(business));
    }

    // Thông báo cho các service biết auth đã thay đổi để reset + reload dữ liệu
    this._onAuthChanged$.next();
  }

  /**
   * Xóa dữ liệu user lưu ở local
   * - Logout
   * - Login thành công thì cũng cần xóa trước
   * */
  clearAuthUser(): void {
    // Đóng mọi DO room trước khi xóa session để tránh leak WebSocket.
    try {
      this.doService.leaveAllRooms();
    } catch { /* no-op */
    }
    if (typeof localStorage !== "undefined") {
      localStorage.clear();
    }
  }


  logout() {
    this._app.showLoading();

    // Lưu cờ isMobileApp trước khi clearAuthUser xóa localStorage
    let isMobileApp = false;
    if (typeof localStorage !== 'undefined') {
      isMobileApp = !!localStorage.getItem('isMobileApp');
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('filterPageIds');
    }

    const loginUri = isMobileApp
      ? '/login-appmobile?logout=1'
      : '/';

    // Hành động xóa session FE và chuyển hướng
    const processFrontendLogout = () => {
      this.clearAuthUser();
      this.document.location.href = loginUri;
    };

    // Gọi API logout lên server trước để hủy session ở BE
    // Dùng this.http trực tiếp để tránh circular dependency:
    // ApiService (parent của LoginApiService) inject AuthService
    this.http.post(environment.baseUrlApi + '/auth/logout', {}).subscribe({
      next: () => {
        // Sau khi BE logout thành công thì mới logout FE
        processFrontendLogout();
      },
      error: (err) => {
        console.error('Lỗi khi gọi API logout:', err);
        // Dù lỗi vẫn ép logout ở FE để tránh kẹt trạng thái
        processFrontendLogout();
      }
    });
  }

  isLoginEd = () => {
    return !!this.getAuthorizationToken;
  }

  hasJoinPages(): boolean {
    // Kiểm tra xem user đã chọn page hay chưa thông qua pageIds hoặc danh sách pages
    const hasIds = this.auth?.pageIds && isArray(this.auth.pageIds) && this.auth.pageIds.length > 0;
    const hasPages = this.auth?.pages && (isArray(this.auth.pages) && this.auth.pages.length > 0 || typeof this.auth.pages === 'object' && Object.keys(this.auth.pages).length > 0);
    return !!(hasIds || hasPages);
  }

  /**
   * Cập nhật dữ liệu Authentication vào hệ thống
   * @param data Đối tượng Auth chứa JWT, User info, Businesses...
   */
  setAuthDataService(data: Auth) {
    if (!data) return;

    // 1. Chuẩn hóa dữ liệu (Đảm bảo businesses luôn là mảng)
    this.normalizeAuthData(data);
    this.auth = data;

    // 2. Đồng bộ Quyền (Permissions) ngay lập tức
    // Sử dụng injector để tránh vòng lặp phụ thuộc (Circular Dependency)
    if (this.auth.role !== undefined) {
      const permissionService = this.injector.get(PermissionService);
      permissionService.setPermissionsByRole(this.auth.role);
    }

    // 3. Thông báo cho các thành phần UI (Header, Menu)
    this._businesses.next(this.getBusinessesManagement());

    // 4. Kiểm tra trạng thái chọn Page để hiển thị Menu
    if (this.hasJoinPages()) {
      this.isSelectPage = true;
    }

    // 5. Khởi tạo doanh nghiệp mặc định
    this.initializeActiveBusiness();

    // 6. Kết nối Durable Object cho user / page / business room.
    // Khi `environment.durable_object.ws_url` rỗng, service tự no-op.
    // @see [ADR-0002](../../../../agent-vpage/tracking/decision-log.md)
    this.syncDurableObjectRooms();
  }

  /**
   * Đồng bộ các DO room theo trạng thái auth hiện tại.
   * Room của Page/Business đã bị bỏ quyền sẽ được đóng, không chỉ join thêm room mới.
   */
  private syncDurableObjectRooms(): void {
    try {
      if (!this.doService.isConfigured()) return;

      this.doService.syncAuthRooms({
        userId: this.auth?.uid,
        pageIds: (this.auth?.pageIds ?? []) as Array<string | number>,
        businessIds: (this.auth?.mBids ?? []) as Array<string | number>,
      });
    } catch (err) {
      // Không bao giờ chặn flow auth vì lỗi DO.
      console.warn('[AuthService] syncDurableObjectRooms failed:', err);
    }
  }

  /**
   * Chuẩn hóa dữ liệu Auth (Xử lý trường hợp Backend trả về Object thay vì Array cho businesses)
   */
  private normalizeAuthData(data: Auth) {
    if (data.businesses && !isArray(data.businesses)) {
      data.businesses = Object.values(data.businesses);
    }
  }


  setAuthorization(data: string): void {
    const auth = JSON.parse(data) as Auth;
    this.auth = auth;
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY.usr, data);
    }

    // Auth callback có thể ghi trực tiếp bản auth đầy đủ, không đi qua
    // setAuthDataService(). Khi đó vẫn phải thay room cũ bằng đúng tập quyền mới.
    if (auth.pages || auth.pageIds || auth.businesses || auth.mBids) {
      this.normalizeAuthData(auth);
      this._businesses.next(this.getBusinessesManagement());
      this.initializeActiveBusiness();
      this.syncDurableObjectRooms();
      this._onAuthChanged$.next();
    }
  }

  updateAuthorization(data: any) {
    if (data?.businesses && data?.pages) {
      this.auth.pages = {};

      for (const page of data.pages) {
        const p: any = {
          id: page.id,
          channel: page.channel,
          name: page.name,
          isManagePage: page.isManagePage,
          isSyncedData: page.isSyncedData
        };

        if (page?.avatar) p.avatar = page.avatar;
        if (page?.token) p.token = page.token;
        if (page?.fbDatasetId) p.fbDatasetId = page.fbDatasetId;
        if (page?.settings) p.settings = page.settings;
        if (page?.aliasName) p.aliasName = page.aliasName;
        if (page?.zaloMobile) p.zaloMobile = page.zaloMobile;

        let bkey = 'other';
        if (page.businessId) {
          bkey = page.businessId;

          p.businessId = page.businessId.toString();
        }

        this.auth.pages[page.id] = p;

        data.businesses[bkey].pages.push({
          id: page.id,
          name: page.name,
          isManagePage: page.isManagePage,
          channel: page.channel
        });
      }

      this.auth.businesses = [];
      for (const bid in data.businesses) {
        this.auth.businesses.push(data.businesses[bid]);
      }
    }

    if (data?.aBids) {
      this.auth.aBids = data.aBids;
      this.io.joinRoom(this.getBusinessAllSelectManagement());
    }

    if (data?.pageIds) {
      this.auth.pageIds = data.pageIds;
      // Join room on socketIO
      this.io.joinRoom(this.getPageIds());
    }

    if (data?.settings) {
      this.auth.settings = data.settings;
    }

    if (data?.role) {
      this.auth.role = data.role;
    }

    // Cập nhật lại mBids nếu có (danh sách ID doanh nghiệp quản lý)
    if (data?.mBids) {
      this.auth.mBids = data.mBids;
    }

    // Cập nhật danh sách doanh nghiệp mới vào Subject để báo cho Header/UI biết
    this._businesses.next(this.getBusinessesManagement());

    // Đồng bộ lại doanh nghiệp đang hoạt động để Header cập nhật đúng thông tin
    this.initializeActiveBusiness();

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('usr', JSON.stringify(this.auth));
    }

    // updateAuthorization được gọi sau cấp/chọn lại quyền Page. Đồng bộ theo
    // tập mới để đóng DO subscriptions của page/business không còn được chọn.
    this.syncDurableObjectRooms();

    // Thông báo cho các service biết auth đã thay đổi để reset + reload dữ liệu (tags, quick msg, settings, ...)
    this._onAuthChanged$.next();
  }

  getUserType() {
    return this.auth.userType;
  }

  getPosUid(): string {
    return this.auth?.posUid ?? '';
  }

  // Kiểm tra người dùng có quản lý doanh nghiệp nào không
  isBusinessManagement() {
    return !!this.auth.mBids?.length;
  }

  /**
   * lấy id những doanh nghiệp đã chọn khi chọn page
   */
  getBusinessAllSelectManagement(): any {
    return this.auth?.aBids;
  }

  getBusinessesManagement(): IBusinessOption[] {
    // Guard: kiểm tra businesses tồn tại trước khi xử lý
    if (!this.auth?.businesses) {
      return [];
    }

    const businessesSource = isArray(this.auth.businesses) ? this.auth.businesses : Object.values(this.auth.businesses);

    // Nếu là Admin/SuperAdmin thì trả về toàn bộ danh sách doanh nghiệp
    if (this.isAdmin()) {
      return businessesSource as IBusinessOption[];
    }

    if (!this.auth?.mBids || !isArray(this.auth.mBids))
      return [];

    const businesses: IBusinessOption[] = [];
    for (const mBid of this.auth.mBids) {
      for (const business of businessesSource as any[]) {
        if (business.id == mBid) {
          businesses.push(business);
        }
      }
    }

    return businesses;
  }

  /** Toàn bộ doanh nghiệp gắn với user, không giới hạn theo quyền quản lý. */
  getBusinesses(): IBusinessOption[] {
    if (!this.auth?.businesses) return [];
    return isArray(this.auth.businesses)
      ? this.auth.businesses as IBusinessOption[]
      : Object.values(this.auth.businesses) as IBusinessOption[];
  }

  /**
   * Lấy danh sách các trang (Page) dựa trên channel và doanh nghiệp đang hoạt động.
   * @param channel (Tùy chọn) lọc theo channel cụ thể hoặc danh sách channel.
   * @returns Danh sách các IPage đã được lọc.
   */
  getPages(channel?: number | number[]): IPage[] {
    const pages: IPage[] = [];
    const activeBusiness = this.getActiveBusiness();

    if (this.auth && this.auth?.pages) {
      // 1. Chuyển object pages thành mảng để xử lý lọc
      let allPages = Object.values(this.auth.pages);

      // 2. Lọc theo doanh nghiệp đang chọn (activeBusiness) từ header
      if (activeBusiness) {
        allPages = allPages.filter(p => p.businessId == activeBusiness.id);
      }

      if (channel) {
        const channels = isArray(channel) ? (channel as number[]) : [channel as number];

        // 3. Lọc tiếp theo channel nếu có tham số truyền vào
        for (const cn of channels) {
          for (const page of allPages) {
            if (cn == page.channel) {
              pages.push(page);
            }
          }
        }
      } else {
        // Nếu không lọc channel, lấy toàn bộ danh sách đã lọc theo business
        pages.push(...allPages);
      }
    }

    return pages;
  }

  /**
   * Lấy danh sách ID của các trang.
   * @param channel (Tùy chọn) nếu truyền vào sẽ lọc theo channel 1 (Facebook)
   */
  getPageIds(channel?: any): string[] {
    if (channel) {
      if (this.auth?.pages) {
        return Object.values(this.auth.pages)
          .filter(page => page.channel === channel)
          .map(page => page.id);
      }
      return [];
    }
    return this.auth.pageIds ?? [];
  }

  getAuthorizationToken(): string {
    if (!this.auth.jwt) {
      return '';
    }

    return `Bearer ${this.auth.jwt}`;
  }

  //----- Đoạn này xử lý đánh dấu đang xử lý hội thoại của page nào
  get pageDisplay() {
    return this._pageDisplay;
  }

  set pageDisplay(value: any) {
    let page = this.auth?.pages?.[value] ?? '';
    if (!page) {
      if (typeof sessionStorage !== "undefined") {
        const storageAffiliate = sessionStorage.getItem('pagesPartnerAfiliate')
        if (storageAffiliate) {
          const pagesPartnerAfiliate = JSON.parse(storageAffiliate)
          if (pagesPartnerAfiliate && pagesPartnerAfiliate?.length > 0) {
            for (const pagePartner of pagesPartnerAfiliate) {
              if (pagePartner.id == value) {
                page = pagePartner;
                break;
              }
            }
          }
        }
      }
    }
    this._pageDisplay.next(page);
  }

  /**
   * Lấy ID người dùng.
   */
  getUid(): string {
    return this.auth?.uid ?? '';
  }

  // Lấy role của user hiện tại, ưu tiên lấy từ activeBusiness nếu có, nếu không thì lấy từ auth.role
  getCurrentRole(): number {
    if (this.hasJoinPages() && this.auth.businesses?.length) {
      const activeBusiness = this.getActiveBusiness();
      if (activeBusiness?.roleId) {
        // Nếu là custom role, trả về role cao nhất để có thể hiển thị menu
        if (this.isCustomRoleByRoleId(activeBusiness.roleId)) {
          return ROLES.Business_Staff; // Hoặc role phù hợp để hiển thị menu
        }
        return this.mapRoleIdToNumber(activeBusiness.roleId);
      }
    }

    return this.auth.role as number;
  }

  // Chuyển đổi roleId từ string sang số dạng cũ, nếu không tìm thấy thì trả về ROLES.Guest
  private mapRoleIdToNumber(roleId: string): number {
    const roleMapping: { [key: string]: number } = {
      'SYSTEM_ROLE_SUPER_ADMIN': ROLES.SuperAdmin,
      'SYSTEM_ROLE_ADMIN': ROLES.Admin,
      'SYSTEM_ROLE_MANAGER': ROLES.Business_Manager,
      'SYSTEM_ROLE_PAGE_ADMIN': ROLES.Page_Admin,
      'SYSTEM_ROLE_STAFF': ROLES.Business_Staff,
      'SYSTEM_ROLE_BLOG_EDITOR': ROLES.Blog_Editor,
      'SYSTEM_ROLE_MEMBER': ROLES.Member,
      'SYSTEM_ROLE_GUEST': ROLES.Guest
    };
    return roleMapping[roleId] || ROLES.Guest;
  }

  isAllowed(role: any): boolean {
    const currentRole = this.getCurrentRole();
    if (currentRole == ROLES.SuperAdmin) {
      return true;
    }
    let allowedRoles = [ROLES.Guest];
    // Init allowed roles
    if (currentRole == ROLES.Member) {
      allowedRoles.push(ROLES.Member);
    } else if (currentRole == ROLES.Business_Staff) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff];
    } else if (currentRole == ROLES.Business_Manager) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin, ROLES.Business_Manager];
    } else if (currentRole == ROLES.Page_Admin) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin];
    } else if (currentRole == ROLES.Blog_Editor) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Blog_Editor];
    } else if (currentRole == ROLES.Admin) {
      allowedRoles = [ROLES.Guest, ROLES.Member, ROLES.Business_Staff, ROLES.Page_Admin, ROLES.Business_Manager, ROLES.Blog_Editor, ROLES.Admin];
    }

    return allowedRoles.includes(role);
  }

  isAdmin() {
    return this.auth.role == ROLES.Admin || this.auth.role == ROLES.SuperAdmin;
  }

  isSuperAdmin() {
    return this.auth.role === ROLES.SuperAdmin;
  }

  outPages() {
    this.io.leaveRoom(this.getPageIds());
    this.io.leaveAffRoom();

    delete this.auth.aBids;
    delete this.auth.pageIds;
    delete this.auth.pages;

    // update logic
    if (isArray(this.auth.businesses)) {
      for (const business of this.auth.businesses) {
        business.pages = [];
      }
    }
    // this.cache.clearAll();

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('usr', JSON.stringify(this.auth));
      sessionStorage.removeItem('filterPageIds');
    }

    this.syncDurableObjectRooms();
  }

  /**
   * Lấy giá trị cài đặt cá nhân của người dùng từ auth.settings.
   * Các setting được load từ API /setting/user khi đăng nhập.
   * Giá trị thường là 1 (bật) / 2 (tắt).
   */
  getSetting(key: string): any {
    return this.auth?.settings?.[key];
  }

  /**
   * Lấy thông tin user đang đăng nhập, hiển thị lên header profile
   */
  getUser(): IUserHeader {
    const info: any = {}

    if (this.auth?.posUid) {
      info.type = 'POS';
      info.picture = 'default_avatar.png';
      info.displayName = this.auth?.displayName ?? '';

      if (this.auth?.fbUid) {
        info.fb = {
          picture: `https://graph.facebook.com/${this.auth.fbUid}/picture?height=100&width=100`,
          displayName: this.auth?.fbName ?? '',
        }
      }
    } else if (this.auth?.userType && this.auth.userType == 2) {
      info.displayName = this.auth.displayName;
    } else {
      info.type = 'FB';
      info.picture = `https://graph.facebook.com/${this.auth.fbUid}/picture?height=100&width=100`;
      info.displayName = this.auth?.fbName ?? '';
    }

    return info;
  }

  get isSelectPage() {
    return this._isSelectPage;
  }

  set isSelectPage(value: any) {
    this._isSelectPage.next(value);
  }

  /**
   * Check có phải là custom role không
   */
  isCustomRole(): boolean {
    const activeBusiness = this.getActiveBusiness();
    if (!activeBusiness?.roleId) return false;

    return this.isCustomRoleByRoleId(activeBusiness.roleId);
  }

  private isCustomRoleByRoleId(roleId: string): boolean {
    const systemRoles = Object.values(ROLES_NEW);
    return !systemRoles.includes(roleId);
  }

  // Hàm để lấy doanh nghiệp đang hoạt động, ưu tiên lấy từ localStorage nếu có, nếu không thì lấy từ auth.businesses
  // Nếu không có doanh nghiệp nào thì trả về null
  getActiveBusiness(): IBusinessOption | null {
    if (!this.auth.businesses || (!isArray(this.auth.businesses) && typeof this.auth.businesses !== 'object')) {
      return null;
    }

    const businessesManagement = this.getBusinessesManagement();
    if (businessesManagement.length === 0) return null;

    // 1. Ưu tiên kiểm tra businessId từ URL query params (Dùng URLSearchParams để đọc ?businessId=)
    let urlBusinessId: string | null = null;
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlBusinessId = urlParams.get('businessId');
    }

    // 2. Lấy từ localStorage nếu có
    let stored = null;
    let parsedBusiness = null;
    if (isPlatformBrowser(this.platformId)) {
      stored = localStorage.getItem('activeBusiness');
      if (stored) {
        try {
          parsedBusiness = JSON.parse(stored);
        } catch (e) {
          // Bỏ qua nếu dữ liệu rác để không làm crash luồng render menu
          localStorage.removeItem('activeBusiness');
        }
      }
    }

    // Xác định ID mục tiêu (ưu tiên URL > LocalStorage)
    const targetId = urlBusinessId || parsedBusiness?.id;

    if (targetId) {
      // Tìm business trong danh sách được quản lý (Đã bao gồm check quyền Admin/SuperAdmin/mBids)
      const currentBusiness = (businessesManagement as any[]).find((b: { id: any; }) => b.id == targetId);

      if (currentBusiness) {
        // Nếu lấy từ URL -> Luôn tin tưởng vì đã check quyền ở trên
        // Nếu lấy từ LocalStorage -> Check thêm roleId để đảm bảo đồng bộ quyền
        if (urlBusinessId || currentBusiness?.roleId === parsedBusiness?.roleId) {
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('activeBusiness', JSON.stringify(currentBusiness));
          }
          return currentBusiness;
        }
      }
    }

    // 3. Fallback: Lấy doanh nghiệp đầu tiên có quyền quản lý
    const firstBus = businessesManagement[0];
    if (isPlatformBrowser(this.platformId) && firstBus) {
      localStorage.setItem('activeBusiness', JSON.stringify(firstBus));
    }
    return firstBus;
  }

  // Hàm để set active business khi vào app
  initializeActiveBusiness() {
    if (!this.auth.businesses?.length || !this.auth.businesses[0]?.roleId) {
      return;
    }

    const stored = this.getActiveBusiness();
    if (stored) {
      this.activeBusiness = stored;
    } else if (this.auth.businesses?.length) {
      this.activeBusiness = this.auth.businesses[0];
    }
  }

  /**
     * Lấy tên người dùng đăng nhập
     */
    getUserDisplayName(): string {
        return this.auth.displayName ?? '';
    }
    getFbName() {
        return this.auth?.fbName ?? '';
    }
}
